import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { buildZip, capturePage, readSiteZip, sleep } from "./siteCode";

export function SiteCodeTools({ store, theme, setPage, setDevice, setSel, page }) {
  const [busy, setBusy] = useState(false);
  const file = useRef();
  const exportCode = async () => {
    setBusy(true);
    const t = toast.loading("Preparing website code…");
    try {
      setSel(null);
      setDevice("desktop");
      const pages = {};
      for (const [k, p] of Object.entries(theme.pages)) {
        toast.loading(`Capturing page: ${p.title}…`, { id: t });
        setPage(k);
        await sleep(3000);
        pages[k] = capturePage(document.querySelector("[data-testid=builder-canvas-frame]"), p, `${store.name} | ${p.title}`);
      }
      setPage(page);
      const blob = await buildZip({ pages, theme, store, onProgress: (m) => toast.loading(m, { id: t }) });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(store.slug || "store").replace(/[^a-z0-9-]/gi, "")}-website-code.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success(`Downloaded ${Object.keys(pages).length} pages`, { id: t });
    } catch (e) { toast.error(errMsg(e) || "Export failed", { id: t }); }
    setBusy(false);
  };
  const importCode = async (f) => {
    if (!f) return;
    if (!window.confirm(`Replace the sections of the pages in "${f.name}" with this code?\n\nIt is saved as a DRAFT only. Your live store doesn't change until you click Publish.`)) return;
    setBusy(true);
    const t = toast.loading("Reading code…");
    try {
      const uploadAsset = async (file) => { const fd = new FormData(); fd.append("file", file); return (await api.post(`/admin/stores/${store.id}/site-code/asset`, fd)).data.url; };
      let body;
      if (/\.html?$/i.test(f.name)) body = { pages: { [f.name.replace(/\.html?$/i, "").toLowerCase().replace(/^index$/, "home")]: await f.text() } };
      else body = await readSiteZip(f, uploadAsset, (m) => toast.loading(m, { id: t }));
      if (!Object.keys(body.pages || {}).length) throw new Error("No .html pages found in this file");
      toast.loading("Building sections…", { id: t });
      const r = await api.post(`/admin/stores/${store.id}/site-code`, body);
      const sum = Object.entries(r.data.pages).map(([k, v]) => `${k}: ${v.code_sections} design + ${v.live_sections} live`).join(", ");
      toast.success(`Imported — ${sum}. Review, then Publish.`, { id: t, duration: 6000 });
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.message && !e.response ? e.message : errMsg(e), { id: t }); }
    setBusy(false);
    file.current.value = "";
  };
  return (
    <div className="flex items-center gap-1 whitespace-nowrap" data-testid="site-code-tools">
      <button onClick={exportCode} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-[#B8924A]/50 hover:text-white disabled:opacity-50" data-testid="site-code-download-btn">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Download code</button>
      <button onClick={() => file.current.click()} disabled={busy} className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-[#B8924A]/50 hover:text-white disabled:opacity-50" data-testid="site-code-upload-btn"><Upload className="h-3.5 w-3.5" />Upload code</button>
      <input ref={file} type="file" accept=".zip,.html,.htm" className="hidden" onChange={(e) => importCode(e.target.files[0])} data-testid="site-code-file-input" />
    </div>
  );
}
